import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';
import swimService from '../../api/services/swimService';
import { ClassLevel } from '../../types/swim';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface SwimProgramFormData {
  id?: string;
  name: string;
  description: string;
  class_level_id: string;
  start_date: string;
  end_date: string;
  recurrence_days: number[];
  recurrence_time: string;
  recurrence_duration: number;
  price_member: string;
  price_non_member: string;
  max_participants: string;
  required_consents: string[];
  target_audience: 'kids' | 'adult' | 'all';
  members_only: boolean;
}

interface Props {
  open: boolean;
  initial?: SwimProgramFormData | null;
  onClose: () => void;
  onSaved: () => void;
}

const CONSENT_OPTIONS = [
  { value: 'liability_waiver', label: 'Liability waiver' },
  { value: 'emergency_pickup', label: 'Emergency pickup authorization' },
  { value: 'medical_info_sharing', label: 'Medical info sharing' },
  { value: 'photo_video', label: 'Photo / video release' },
  { value: 'swimmer_ability_attestation', label: 'Swimmer ability self-attestation' },
];

const DAY_OPTIONS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export const emptyForm: SwimProgramFormData = {
  name: '',
  description: '',
  class_level_id: '',
  start_date: '',
  end_date: '',
  recurrence_days: [],
  recurrence_time: '16:00',
  recurrence_duration: 60,
  price_member: '',
  price_non_member: '',
  max_participants: '10',
  required_consents: [],
  target_audience: 'kids',
  members_only: false,
};

const SwimProgramFormDialog: React.FC<Props> = ({ open, initial, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [form, setForm] = useState<SwimProgramFormData>(initial ?? emptyForm);
  const [forceRegen, setForceRegen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!form.id;

  useEffect(() => {
    if (open) {
      setForm(initial ?? emptyForm);
      setForceRegen(false);
      setError(null);
      void swimService.listClassLevels().then(setLevels).catch(() => undefined);
    }
  }, [open, initial]);

  function toggleDay(d: number) {
    setForm((p) => ({
      ...p,
      recurrence_days: p.recurrence_days.includes(d)
        ? p.recurrence_days.filter((x) => x !== d)
        : [...p.recurrence_days, d].sort((a, b) => a - b),
    }));
  }

  function toggleConsent(c: string) {
    setForm((p) => ({
      ...p,
      required_consents: p.required_consents.includes(c)
        ? p.required_consents.filter((x) => x !== c)
        : [...p.required_consents, c],
    }));
  }

  async function save() {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.start_date || !form.end_date) { setError('Start and end dates are required'); return; }
    if (!form.price_member) { setError('Member price is required'); return; }
    if (!form.max_participants) { setError('Max participants is required'); return; }

    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        program_type: 'swim',
        class_level_id: form.class_level_id || null,
        target_audience: form.target_audience,
        members_only: form.members_only,
        start_date: form.start_date,
        end_date: form.end_date,
        price_member: Number(form.price_member),
        price_non_member: form.price_non_member ? Number(form.price_non_member) : null,
        max_participants: Number(form.max_participants),
        required_consents: form.required_consents.length ? form.required_consents : null,
        recurrence_pattern:
          form.recurrence_days.length > 0
            ? {
                days: form.recurrence_days,
                time: form.recurrence_time,
                duration: form.recurrence_duration,
              }
            : null,
      };

      if (isEdit && form.id) {
        if (forceRegen) payload.force_regenerate_sessions = true;
        await axiosInstance.put(`/programs/${form.id}`, payload);
      } else {
        await axiosInstance.post('/programs/', payload);
      }
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isEdit ? 'Edit swim program' : 'New swim program'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Audience</InputLabel>
              <Select
                value={form.target_audience}
                label="Audience"
                onChange={(e) =>
                  setForm({
                    ...form,
                    target_audience: e.target.value as SwimProgramFormData['target_audience'],
                    // Selecting a different audience invalidates the
                    // current skill-level pick (it belongs to the old
                    // ladder). Clear it so the next dropdown reflects
                    // the new ladder cleanly.
                    class_level_id: '',
                  })
                }
              >
                <MenuItem value="kids">Kids (dependents)</MenuItem>
                <MenuItem value="adult">Adults (e.g. Masters)</MenuItem>
                <MenuItem value="all">All members</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Swim skill level</InputLabel>
              <Select
                value={form.class_level_id}
                label="Swim skill level"
                onChange={(e) => setForm({ ...form, class_level_id: e.target.value })}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {levels
                  .filter((l) => l.is_active)
                  // Only show levels in the same ladder as the program. For
                  // 'all'-audience programs, show every level so the admin
                  // can still pick one.
                  .filter((l) =>
                    form.target_audience === 'all'
                      ? true
                      : (l.target_audience || 'kids') === form.target_audience,
                  )
                  .map((l) => (
                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.members_only}
                onChange={(e) => setForm({ ...form, members_only: e.target.checked })}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Members only</Typography>
                <Typography variant="caption" color="text.secondary">
                  Hides this program from non-members and blocks them at enrollment.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />

          <Divider textAlign="left"><Typography variant="caption">Schedule</Typography></Divider>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date"
              label="Start"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
            <TextField
              type="date"
              label="End"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </Stack>
          <Box>
            <FormLabel>Days of week</FormLabel>
            <FormGroup row sx={{ mt: 1 }}>
              {DAY_OPTIONS.map((d) => (
                <FormControlLabel
                  key={d.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.recurrence_days.includes(d.value)}
                      onChange={() => toggleDay(d.value)}
                    />
                  }
                  label={d.label}
                />
              ))}
            </FormGroup>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="time"
              label="Time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.recurrence_time}
              onChange={(e) => setForm({ ...form, recurrence_time: e.target.value })}
            />
            <TextField
              type="number"
              label="Duration (min)"
              fullWidth
              value={form.recurrence_duration}
              onChange={(e) => setForm({ ...form, recurrence_duration: Number(e.target.value) })}
            />
          </Stack>

          <Divider textAlign="left"><Typography variant="caption">Pricing & capacity</Typography></Divider>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label="Member price ($)"
              required
              fullWidth
              value={form.price_member}
              onChange={(e) => setForm({ ...form, price_member: e.target.value })}
            />
            <TextField
              type="number"
              label="Non-member price ($)"
              fullWidth
              value={form.price_non_member}
              onChange={(e) => setForm({ ...form, price_non_member: e.target.value })}
            />
          </Stack>
          <TextField
            type="number"
            label="Max participants"
            required
            fullWidth
            value={form.max_participants}
            onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
          />

          <Divider textAlign="left"><Typography variant="caption">Required consents</Typography></Divider>
          <FormGroup>
            {CONSENT_OPTIONS.map((c) => (
              <FormControlLabel
                key={c.value}
                control={
                  <Checkbox
                    size="small"
                    checked={form.required_consents.includes(c.value)}
                    onChange={() => toggleConsent(c.value)}
                  />
                }
                label={c.label}
              />
            ))}
          </FormGroup>

          {isEdit && (
            <>
              <Divider textAlign="left"><Typography variant="caption">Danger zone</Typography></Divider>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={forceRegen}
                    onChange={(e) => setForceRegen(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Force regenerate sessions</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Discards any saved workouts, notes, and one-off session cancellations,
                      and rebuilds the calendar from the schedule above. Attendance and parent
                      RSVPs are never discarded — if any exist, the save will refuse.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => void save()} variant="contained" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create program'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwimProgramFormDialog;
